import React, { useState, useEffect } from 'react'
import { logger } from '../utils/logger'

interface PriceModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: { price: number; delivery_time: number; comment: string }) => Promise<void>
  orderId: string
  orderTitle?: string
  isEdit?: boolean // true для "Изменить цену", false для "Предложить цену"
  initialPrice?: number
  initialDeliveryTime?: number
  initialComment?: string
}

export const PriceModal: React.FC<PriceModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  orderId,
  orderTitle,
  isEdit = false,
  initialPrice,
  initialDeliveryTime,
  initialComment
}) => {
  const [price, setPrice] = useState<string>('')
  const [deliveryTime, setDeliveryTime] = useState<string>('')
  const [comment, setComment] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<{
    price?: string
    deliveryTime?: string
    general?: string
  }>({})

  // Инициализация полей при открытии модального окна
  useEffect(() => {
    if (isOpen) {
      setPrice(initialPrice ? initialPrice.toString() : '')
      setDeliveryTime(initialDeliveryTime ? initialDeliveryTime.toString() : '')
      setComment(initialComment || '')
      setErrors({})
      setIsSubmitting(false)
    }
  }, [isOpen, initialPrice, initialDeliveryTime, initialComment])

  // Закрытие по Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isSubmitting) {
        onClose()
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Блокируем скролл страницы
      document.body.style.overflow = 'hidden'
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, isSubmitting, onClose])

  // Функция для валидации ввода только цифр с десятичными знаками для цены
  const handlePriceInput = (value: string) => {
    // Разрешаем только цифры, точку и запятую (заменяем запятую на точку)
    const cleanValue = value.replace(/[^0-9.,]/g, '').replace(',', '.')
    // Убираем лишние точки (оставляем только первую)
    const parts = cleanValue.split('.')
    const formattedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : cleanValue
    setPrice(formattedValue)
  }

  // Функция для валидации ввода только целых чисел для срока изготовления
  const handleDeliveryTimeInput = (value: string) => {
    // Разрешаем только цифры
    const cleanValue = value.replace(/[^0-9]/g, '')
    setDeliveryTime(cleanValue)
  }

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {}

    // Валидация цены
    const priceNum = parseFloat(price)
    if (!price || isNaN(priceNum) || priceNum <= 0) {
      newErrors.price = 'Введите корректную цену больше нуля'
    } else if (priceNum > 999999) {
      newErrors.price = 'Цена не может превышать 999,999 руб.'
    }

    // Валидация срока изготовления
    const deliveryNum = parseInt(deliveryTime)
    if (!deliveryTime || isNaN(deliveryNum) || deliveryNum < 1 || deliveryNum > 365) {
      newErrors.deliveryTime = 'Введите срок изготовления от 1 до 365 рабочих дней'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm() || isSubmitting) {
      return
    }

    setIsSubmitting(true)
    setErrors({})

    try {
      await onSubmit({
        price: parseFloat(price),
        delivery_time: parseInt(deliveryTime),
        comment: comment.trim()
      })
      onClose()
    } catch (error: any) {
      logger.error('Ошибка при отправке цены:', error)
      setErrors({
        general: error.message || 'Произошла ошибка при отправке цены'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        backdropFilter: "blur(20px)"
      }}
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Заголовок */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEdit ? 'Изменить цену' : 'Предложить цену'}
            </h2>
            {orderTitle && (
              <p className="text-sm text-gray-600 mt-1">{orderTitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Форма */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Общая ошибка */}
          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-600 text-sm">{errors.general}</p>
            </div>
          )}

          {/* Поле цены */}
          <div>
            <label htmlFor="price-input" className="block text-sm font-medium text-gray-700 mb-2">
              Цена (руб./шт.) *
            </label>
            <input
              id="price-input"
              type="text"
              inputMode="decimal"
              value={price}
              onChange={(e) => handlePriceInput(e.target.value)}
              disabled={isSubmitting}
              required
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 ${
                errors.price ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Введите цену"
              autoFocus
            />
            {errors.price && (
              <p className="text-red-600 text-sm mt-1">{errors.price}</p>
            )}
          </div>

          {/* Поле срока изготовления */}
          <div>
            <label htmlFor="delivery-time-input" className="block text-sm font-medium text-gray-700 mb-2">
              Срок изготовления (рабочих дней) *
            </label>
            <input
              id="delivery-time-input"
              type="text"
              inputMode="numeric"
              value={deliveryTime}
              onChange={(e) => handleDeliveryTimeInput(e.target.value)}
              disabled={isSubmitting}
              required
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 ${
                errors.deliveryTime ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Введите срок в днях"
            />
            {errors.deliveryTime && (
              <p className="text-red-600 text-sm mt-1">{errors.deliveryTime}</p>
            )}
          </div>

          {/* Поле комментария */}
          <div>
            <label htmlFor="comment-input" className="block text-sm font-medium text-gray-700 mb-2">
              Комментарий (необязательно)
            </label>
            <textarea
              id="comment-input"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={isSubmitting}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 resize-none"
              placeholder="Дополнительная информация..."
            />
          </div>

          {/* Кнопки */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex-1 px-4 py-2 text-white rounded-lg hover:shadow-lg active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${
                isEdit 
                  ? 'bg-amber-500 hover:bg-amber-600' 
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Отправка...
                </>
              ) : (
                isEdit ? 'Изменить цену' : 'Предложить цену'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PriceModal
